import { Component, createRef, useRef } from 'react';
import { debounceTime, delay, mergeMap, retry } from 'rxjs/operators';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { DropboxFile } from '../../../models/dropbox-file';
import { GoogleDriveFile } from '../../../models/google-drive-file';
import { Logger } from '../../../models/logger';
import { StagedStatus, UploadInProgressFile } from '../../../models/upload-in-progress-file';
import { FileProcessService, ProbedData } from '../../../services/file-process.service';
import { ThumbnailService } from '../../../services/thumbnail.service';
import { GifEstimateSourceKind, gifEstimateSourceKind, gifEstimateStore } from '../../../utils/gif-estimate.store';
import { ConvertOptionCategories } from '../../../types/dropdown.type';
import { InputFileInfo } from '../../../types/file-upload.types';
import { ToType } from '../../../types/types';
import { isThumbnailSupported } from '../../../utils/file';
import { isBlocked } from '../../../utils/convert-eligible';
import { untilDestroyed } from '../../../utils/until-destroyed';
import { ConvertOptionsContext } from '../../convert-options/convert-options-context';
import ProgressComponent from '../progress/progress.component';
import styles from './file-card.component.module.scss';
import { ImageDisplayComponent } from './image-display/image-display.component';
import { of } from 'rxjs';
import { OptionalChildrenProps } from '../../../types/react.types';
import Tooltip from '../tooltip/tooltip.component';
import { FileInfoBoxComponent } from './image-display/file-info-box.component';
import { TextWithLink } from '../unsupported-input/text-with-link';
import { ConvertOptionsContextObserver, ConvertOptionsProvider } from '../../convert-options-v2/convert-options.context';


export interface UpdateProcessProbedDataFnProvider {
    updateProcessProbedDataFn: () => (probeDuration: number) => Promise<any>;
}

interface FileCardComponentProps extends OptionalChildrenProps {
    uploadInProgressFile: UploadInProgressFile<File | GoogleDriveFile | DropboxFile>;
    convertOptionsContext: ConvertOptionsContextObserver;
    convertOptionCategories: ConvertOptionsProvider;
    updateProcessProbedDataFnProvider: UpdateProcessProbedDataFnProvider;
    toType: ToType | string;
    fromType: string[],
    processType?: string;
    removeFile: (data: any) => void;
    hideRemoveButton?: boolean;
    orientationChanged: (data: any) => void;
}

interface FileCardComponentState {
    canDisplayThumbnail: boolean;
    probeFailed: boolean;
}


export default class FileCardComponent extends Component<FileCardComponentProps, FileCardComponentState> {
    private readonly logger = Logger.getLogger('FileCardComponent');

    inputFileInfo: InputFileInfo;
    private static inputFileInfoCache: { [key: string]: InputFileInfo } = {};
    probing = false;
    probedData: ProbedData;
    imageCompRef = createRef<ImageDisplayComponent>()

    @lazyInject(ThumbnailService) private thumbnailService: ThumbnailService;
    @lazyInject(FileProcessService) private fileProcessService: FileProcessService;

    readonly state: Readonly<FileCardComponentState> = {
        canDisplayThumbnail: false,
        probeFailed: false,
    };
    constructor(props: FileCardComponentProps) {
        super(props);
    }

    private get cacheKey(): string {
        const file = this.props.uploadInProgressFile.getFile();
        return `${file.name}_${file.size}`;
    }

    componentDidMount() {
        this.props.convertOptionsContext.modelChangeObserver.pipe(
            untilDestroyed(this),
            debounceTime(100),
        ).subscribe(() => {
            this.forceUpdate();
        });
        this.props.uploadInProgressFile.changes.pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.watchCompletion();
                this.forceUpdate();
            }
        });

        this.watchCompletion();

        this.beginGifEstimateTracking();

        if (this.props.uploadInProgressFile) {
            // TODO Preact fix possible issue
            const canDisplayThumbnail = isThumbnailSupported(this.props.uploadInProgressFile.getFile().name);
            this.setState({
                canDisplayThumbnail: canDisplayThumbnail,
            });
        }
    }

    /** The `whenComplete()` promise this card is waiting on (FU2-01). */
    private watchedCompletion: Promise<unknown> | null = null;

    /**
     * Load the file details once the upload completes. FU2-01 (fu-r3): a failed upload rejects its `whenComplete()` and
     * an in-place Retry gives the row a NEW promise (`UploadInProgressFile.retry`), announced on `changes`. Reading it
     * again on every change -- not once at mount -- is what makes a failed-then-retried row show its duration, bitrate
     * and size instead of skeleton bars that never go away. Each promise is attached once; a superseded one loads nothing.
     */
    private watchCompletion() {
        const completion = this.props.uploadInProgressFile.whenComplete();
        if (!completion || completion === this.watchedCompletion) return;
        this.watchedCompletion = completion;
        completion.then(() => {
            if (this.watchedCompletion === completion) this.loadInputFileInfo();
        }).catch((err) => {
            console.error(err);
        });
    }

    /**
     * GIF-output estimate wiring (docs: GIF_EXACT_SIZE_DESIGN.md §6) — wiring
     * only, all logic lives in gifEstimateStore. Null when this page/file pair
     * isn't a GIF-estimate context.
     */
    private get gifEstimateKind(): GifEstimateSourceKind | null {
        if ((this.props.toType || '').toString().toLowerCase() !== 'gif') {
            return null;
        }
        const file: any = this.props.uploadInProgressFile.getFile();
        const kind = gifEstimateSourceKind(file?.name);
        return kind === 'other' ? null : kind;
    }

    private beginGifEstimateTracking() {
        const kind = this.gifEstimateKind;
        if (!kind) return;
        const upload = this.props.uploadInProgressFile;
        const file: any = upload.getFile();
        if (kind === 'gif') {
            // compress-gif: the natural size IS the current size — synchronous.
            gifEstimateStore.recordKnownNaturalSize(upload.id, file?.size);
            return;
        }
        // Stage 0: client metadata attempt (attempt-based; failure just waits for Stage 1)
        gifEstimateStore.beginClientEstimate(upload.id, file);
        // Stage 2: sample-refined probe once the uploaded-file reference exists
        upload.whenResponseReady()
            .then(() => this.runGifServerProbe())
            .catch(() => undefined);
    }

    private runGifServerProbe() {
        const upload = this.props.uploadInProgressFile;
        const originalInputFile = upload.getResponse();
        const processorMetadata = upload.getMetadata()?.getProcessorMetadata(0);
        if (!originalInputFile || !processorMetadata) return;
        // Single-flight / stale-drop / once-per-file live in the store.
        gifEstimateStore.runServerProbe(upload.id, () => this.fileProcessService.probeSourceForInfo({
            processorMetadata,
            originalInputFiles: [originalInputFile],
            id: upload.id,
            fromType: this.props.fromType,
            toType: this.props.toType as string,
            options: this.props.convertOptionCategories.getSummary(),
            estimateOnly: false,
        })).catch(() => undefined);
    }

    async probe(duration) {
        if (!this.probing) {
            this.probing = true;
            if (this.props?.updateProcessProbedDataFnProvider) {
                const probedData = await this.props.updateProcessProbedDataFnProvider.updateProcessProbedDataFn()(duration);
                this.probedData = probedData;
                // const data = await probedData();
                this.probing = false;
                this.logger.trace7({ message: 'User probed file data', data: probedData });
            }
        }
    }

    private loadInputFileInfo() {
        this.setState({ probeFailed: false });
        of({}).pipe(
            delay(FileCardComponent.inputFileInfoCache[this.cacheKey] ? 0 : 2000),
            mergeMap(() => {
                if (FileCardComponent.inputFileInfoCache[this.cacheKey]) {
                    return of(FileCardComponent.inputFileInfoCache[this.cacheKey]);
                }
                return this.thumbnailService.getInputFileInfo(
                    this.props.uploadInProgressFile.getMetadata().getProcessorMetadata(0)
                );
            }),
            retry({ count: 1, delay: 3000 }),
        ).subscribe({
            next: (inputFileInfo) => {
                this.inputFileInfo = inputFileInfo;
                FileCardComponent.inputFileInfoCache[this.cacheKey] = this.inputFileInfo;
                // Stage 1: piggyback the ffprobe basics onto the gif estimate —
                // same fetch, no extra HTTP call.
                if (this.gifEstimateKind === 'video') {
                    gifEstimateStore.recordInputFileInfo(this.props.uploadInProgressFile.id, inputFileInfo);
                }
                this.setState({ probeFailed: false });
            },
            error: (err) => {
                this.logger.warn({ message: 'File info probe failed, continuing without metadata.', error: err });
                this.setState({ probeFailed: true });
            }
        });
    }
    getHtml() {
        const uploadProgress = this.props.uploadInProgressFile.getProgress();
        const isCompleted = this.props.uploadInProgressFile.completed;
        const hasError = this.props.uploadInProgressFile.error;

        return (
            <div className={styles['file-card']}>
                {!isCompleted && (
                    <>
                        <div
                            className={styles['upload-progress-overlay']}
                            style={{ width: `${uploadProgress}%` }}
                        />
                        <div className={`${styles['upload-badge']} badge ${hasError ? 'text-bg-danger' : 'text-bg-primary'}${
                            hasError && this.props.uploadInProgressFile.canRetry ? ` ${styles['upload-badge-retry']}` : ''}`}>
                            {hasError ? <>{this.props.uploadInProgressFile.stagedStatus?.code === 'enforcement_refused' ? 'Not uploaded' : 'Upload Failed'}{this.props.uploadInProgressFile.canRetry &&
                                <button type="button" className={`btn btn-sm btn-outline-light ms-2 ${styles['upload-retry-btn']}`}
                                    data-upload-retry={this.props.uploadInProgressFile.uuid}
                                    onClick={() => this.props.uploadInProgressFile.retry()}>Retry</button>
                            }</> : `${uploadProgress}% Uploaded`}
                        </div>
                    </>
                )}

                <div className={styles['file-card-image']} style={{ position: 'relative' }}>
                    <ImageDisplayComponent
                        key={this.props.uploadInProgressFile.uuid + isCompleted}
                        ref={this.imageCompRef}
                        uploadInProgressFile={this.props.uploadInProgressFile}
                        file={this.props.uploadInProgressFile.getFile()}
                        convertOptionCategories={this.props.convertOptionCategories}
                        convertOptionsContext={this.props.convertOptionsContext}
                        probedData={this.probedData}
                        probing={this.probing}
                        toType={this.props.toType}
                        fromType={this.props.fromType}
                        processType={this.props.processType}
                        inputFileInfo={this.inputFileInfo}
                        removeFile={$event => this.props.removeFile($event)}
                        orientationChanged={$event => this.props.orientationChanged($event)}
                        probe={$event => this.probe($event)}
                    />
                    {this.state.probeFailed && !isBlocked(this.props.uploadInProgressFile) && (
                        <div className={styles['error-overlay']}>
                            <span className={styles['error-text']}>Failed to load preview</span>
                            <button className={styles['retry-btn']} onClick={() => this.loadInputFileInfo()}>Retry</button>
                        </div>
                    )}
                </div>

                <div className={styles['file-card-body']}>
                    <div className={styles['title']}>
                        <Tooltip text={this.props.uploadInProgressFile.getFile().name}>
                            {this.props.uploadInProgressFile.getFile().name}
                        </Tooltip>
                    </div>
                    {this.getStagedStatus()}

                    {/* FIX-6 (UX2-03): a blocked row shows no probe chips (bitrate, FPS, frames) -- they contradict "Can't
                        convert" (a vector SWF reports a video bitrate it does not have) -- and no skeleton. */}
                    {isBlocked(this.props.uploadInProgressFile) ? null : this.inputFileInfo ? (
                        <div className={styles['metadata-container']}>
                            <FileInfoBoxComponent
                                fileInfo={this.inputFileInfo}
                                convertOptionsProvider={this.props.convertOptionCategories}
                                convertOptionsContext={this.props.convertOptionsContext}
                                toType={this.props.toType}
                                fromType={this.props.fromType}
                            />
                        </div>
                    ) : (
                        <div className={styles['metadata-container']}>
                            <div className={styles['skeleton-row']}>
                                <span className={styles['skeleton-tag']}></span>
                                <span className={styles['skeleton-tag']} style={{ width: 72 }}></span>
                                <span className={styles['skeleton-tag']} style={{ width: 56 }}></span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={`${styles['action-sidebar']} not-sort-handler`}>
                    <div className={styles['tools-column']}>
                        {!this.imageCompRef.current?.error && this.imageCompRef.current?.shouldShowRotateAndFlip() && (
                            <>
                                {/* QA4-09: each key is named by its aria-label; the icon is decoration. */}
                                <button
                                    type="button"
                                    className={styles['action-item']}
                                    title="Rotate"
                                    aria-label="Rotate"
                                    onClick={(e) => this.imageCompRef.current?.rotateFileFn(this.imageCompRef.current?.toolsEnabled, e)}
                                >
                                    <img src="/icons/svg/arrow-counterclockwise.svg" alt="" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className={styles['action-item']}
                                    title="Flip Horizontal"
                                    aria-label="Flip horizontally"
                                    onClick={(e) => this.imageCompRef.current?.flipFileHFn(this.imageCompRef.current?.toolsEnabled, e)}
                                >
                                    <img src="/icons/svg/arrow-left-right.svg" alt="" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className={styles['action-item']}
                                    title="Flip Vertical"
                                    aria-label="Flip vertically"
                                    onClick={(e) => this.imageCompRef.current?.flipFileVFn(this.imageCompRef.current?.toolsEnabled, e)}
                                >
                                    <img src="/icons/svg/arrow-down-up.svg" alt="" aria-hidden="true" />
                                </button>
                            </>
                        )}
                    </div>
                    {!this.props.hideRemoveButton && <div className={styles['remove-column']}>
                        <button
                            type="button"
                            className={`${styles['action-item']} ${styles['remove-btn']}`}
                            title="Remove"
                            aria-label={`Remove ${this.props.uploadInProgressFile.getFile()?.name || 'file'}`}
                            onClick={() => this.props.removeFile(this.props.uploadInProgressFile.getFile())}
                        >
                            <img src="/icons/svg/x.svg" alt="" aria-hidden="true" />
                        </button>
                    </div>}
                </div>
            </div>
        );
    }

    /**
     * The row's status line under its name (prod fixes 2026-10-02, PLAN.md C6). The polite region is always
     * mounted (empty, so it takes no space) and a `live` status renders INSIDE it, so a screen reader hears a
     * status that arrived in the background; a non-live status renders beside it, unannounced. Warnings and
     * blocks lead with ⚠ and a visually hidden "Warning:", info with ⓘ and "Note:" -- never colour alone.
     */
    getStagedStatus() {
        const status = this.props.uploadInProgressFile?.stagedStatus ?? null;
        const line = status && (
            <div className={styles['staged-status']} data-jkey="staged-status" data-staged-code={status.code} data-staged-kind={status.kind}>
                <span aria-hidden="true">{status.kind === 'info' ? 'ⓘ ' : '\u26A0\uFE0E '}</span>
                <span className="sr-only">{status.kind === 'info' ? 'Note: ' : 'Warning: '}</span>
                {status.title
                    ? <>
                        <strong className={styles['staged-status-title']} data-jkey="staged-status-title">{status.title}</strong>
                        <span className="sr-only">. </span>
                        <span className={styles['staged-status-reason']}>{FileCardComponent.stagedStatusText(status)}</span>
                    </>
                    : FileCardComponent.stagedStatusText(status)}
                {status.action && <>
                    {' '}
                    <button type="button" className={styles['staged-status-action']} data-jkey="staged-status-action"
                        onClick={() => status.action?.onClick()}>{status.action.label}</button>
                </>}
            </div>
        );
        return <>
            <div aria-live="polite" data-jkey="staged-status-live">{status?.live ? line : null}</div>
            {status && !status.live ? line : null}
        </>;
    }

    /** FIX-6: `text` with its optional `link` (the Ruffle player) as a plain link in a new tab. */
    static stagedStatusText(status: StagedStatus) {
        return <TextWithLink text={status.text} link={status.link} className={styles['staged-status-link']} jkey="staged-status-link" />;
    }

    render() {
        return (
            this.getHtml()
        )
    }
}
